import { ActionModal } from '@kcftech/design-system';
import { IAssemblyApplicationResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import {
    AssemblyApplicationFormContent,
    AssemblyApplicationFormData,
} from 'pages/Settings/AssemblyType/AssemblyApplicationFormContent';
import { useForm } from 'react-hook-form';
import { updateAssemblyApplication } from 'utils/assembly_application';

export type ApplicationEditInfo = Readonly<{
    application: IAssemblyApplicationResponse;
    siblings: IAssemblyApplicationResponse[];
}>;

export type EditAssemblyApplicationProps = ApplicationEditInfo &
    Readonly<{
        onClose: (updatedApplication?: IAssemblyApplicationResponse) => void;
    }>;

export function EditAssemblyApplicationModal({
    application,
    siblings,
    onClose,
}: EditAssemblyApplicationProps) {
    const siblingNames = siblings.map((sibling) => sibling.Name);
    const siblingCodes = siblings.map((sibling) => sibling.Code);

    const { showToast } = useNotificationContext();
    const form = useForm<AssemblyApplicationFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: application.Name,
            code: application.Code,
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
    } = form;

    const { mutate: updateApplication, isPending } = useMutation({
        meta: { name: 'update-assembly-application' },
        mutationFn: async (data: AssemblyApplicationFormData) =>
            updateAssemblyApplication(application.Id, (req) => {
                req.Name = data.name.trim();
            }),

        onSuccess: (result) => {
            onClose(result);
            showToast({
                type: 'success',
                content: 'Successfully updated assembly application',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update assembly application',
            }),
    });

    const onUpdate = handleSubmit((formData) => updateApplication(formData));

    return (
        <ActionModal
            onAction={onUpdate}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={() => onClose()}
            title="Edit Assembly Application"
            height={350}
            actionButtonText="Save"
            closeButtonText="Cancel"
        >
            <AssemblyApplicationFormContent
                isEdit
                form={form}
                siblingNames={siblingNames}
                siblingCodes={siblingCodes}
            />
        </ActionModal>
    );
}
